import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'moment/locale/it';
import { useHistory } from 'react-router';
import OrderItem from './OrderItem';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  returnOrderRequestStatuses,
} from '../../utils/format';
import { isOrderConfirmed } from '../../utils/order';
import { canShowPartnerInfo } from '../../utils/canShowPartnerInfo';
import { ORDER_STATUSES, ORDER_TYPES } from '../../define/constants';
import endpoints from '../../define/endpoints';
import { getAcceptedTerms } from '../../hooks/getAcceptedTerms';

import { Icon, InlineIcon } from '@iconify/react';
import iconMapMarker from '@iconify-icons/mdi/map-marker';
import { Popup } from '../Popup/Popup';
import { Timeline } from '../Timeline/Timeline';
import { TRANSLATIONS } from '../../../translations';
import OrderStatusInfo from './OrderStatusInfo';
import { PURCHASE_TYPE } from '../../../constants';
import { ModalOrderCard } from '../modals/subComponents/ModalOrderCard';
import { GIFTCARD_COVER_URL } from '../../../constants';

const REQUEST_TYPES = {
  refund: ['completed_booking_refund', 'confirmed_booking_refund'],
  reschedule: ['completed_booking_new_date', 'confirmed_booking_new_date'],
  voucher: ['completed_booking_to_voucher', 'confirmed_booking_to_voucher'],
};
const requestStatusesToShowCard = [
  ...REQUEST_TYPES.refund,
  ...REQUEST_TYPES.reschedule,
  ...REQUEST_TYPES.voucher,
];

const OrderDetails = ({ onCloseClick, order }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const history = useHistory();
  if (!order) {
    history.goBack();
    return null;
  }
  const [open, setOpen] = useState(false);
  const { listing, partner } = order;
  const acceptedTerms = order?.id ? getAcceptedTerms(order.id) : null;
  const handleCloseTerms = () => {
    setOpen(false);
  };
  const handleOpenTerms = () => {
    setOpen(true);
  };
  const coverImage =
    order.listing.coverImage && order.type !== ORDER_TYPES.giftcard
      ? order.listing.coverImage
      : GIFTCARD_COVER_URL;

  let values = null;
  let timelineTitle = null;
  if (order.customerRequestType && order.customerRequestStatus) {
    values = returnOrderRequestStatuses(
      order.customerRequestType,
      order.customerRequestStatus,
      true
    );
    if (order.customerRequestType === 'voucher_to_giftcard') {
      timelineTitle = TRANSLATIONS.ACTIVITY_CHANGE_REQUEST_STATUS;
    } else if (order.customerRequestType === 'voucher_withdraw') {
      timelineTitle = TRANSLATIONS.CANCELLATION_REQUEST_STATUS;
    } else if (order.customerRequestType === 'completed_booking_to_voucher') {
      timelineTitle = TRANSLATIONS.TRANSFORM_TO_VOUCHER_REQUEST_STATUS;
    } else if (order.customerRequestType === 'completed_booking_refund') {
      timelineTitle = TRANSLATIONS.REFUND_REQUEST_STATUS;
    }
  }

  const timelineSteps = values
    ? [
      {
        icon:
          order.customerRequestType === 'completed_booking_to_voucher' ||
            order.customerRequestType === 'completed_booking_refund'
            ? 'refund'
            : 'message',
        status: 'approved',
        title: {
          text: values.initialStep.title,
          bold: true,
        },
        description: values.initialStep.description,
      },
      {
        icon: 'spinner-dotted',
        status: 'approved',
        title: {
          text: values.pending.title,
          bold: true,
        },
        description: values.pending.description,
      },
      {
        icon:
          order.customerRequestStatus == 'rejected' ? 'cross' : 'document',
        status: order.customerRequestStatus,
        title: {
          text: values.outcome.title,
          bold: true,
        },
        description: values.outcome.description,
      },
    ]
    : [];

  return (
    <>
      {(order.type === 'voucher' || order.type === 'booking') && values && (
        <section className="order-details__section">
          <div className="order-details">
            <h2 className="order-details__section__title mb-xs">
              {timelineTitle}
            </h2>
            <Timeline isVertical={true} timelineSteps={timelineSteps} />
          </div>
        </section>
      )}

      <div className="order-details">
        <Popup
          isOpen={open}
          onClickOverlay={() => handleCloseTerms()}
          shouldScroll={false}
        >
          <div className="popup-modal-terms-container">
            <div
              className="popup-modal-terms-content"
              dangerouslySetInnerHTML={{ __html: acceptedTerms }}
            />
            <div className="popup-modal-terms-col">
              <button className="close-button" onClick={handleCloseTerms}>
                &times;
              </button>
            </div>
          </div>
        </Popup>

        <section className="order-details__section">
          <h2 className="order-details__section__title">
            {app_main_wpml.recap}
          </h2>
          <div className="entry__header">
            <div className="entry__intro">
              {order.type !== PURCHASE_TYPE.GIFTCARD && (
                <div className="entry__address text-has-icon">
                  <Icon icon={iconMapMarker} className="icon" />
                  {listing.city}
                  {listing.city !== listing.province && ', ' + listing.province}
                </div>
              )}

              <div className="entry__title">
                {order.listing.title}
                {order.product.title ? ` - ${order.product.title}` : ''}
              </div>
            </div>
            <div className="entry__image">
              <img
                srcSet={`
                  ${endpoints.bucket}${coverImage}?smart&width=200 1x, 
                  ${endpoints.bucket}${coverImage}?smart&width=400 2x,
                `}
                alt={order.listing.title}
                onError={(e) => {
                  e.target.src =
                    endpoints.bucket + 'images/listing-cover-placeholder.jpg';
                }}
              />
            </div>
          </div>
          <div className="entry-recap">
            {isOrderConfirmed(order.status) && (
              <React.Fragment>
                <div>
                  <div className="entry__label">
                    {app_main_wpml.meeting_point}
                  </div>
                  <div className="entry__value">{listing.meetingPoint}</div>
                </div>
                {listing.advanceMeetingMinutes > 0 && (
                  <div>
                    <div className="entry__label">
                      {app_main_wpml.meeting_hour}
                    </div>
                    <div className="entry__value">
                      {listing.advanceMeetingMinutes}{' '}
                      {app_main_wpml.minutes_before}
                    </div>
                  </div>
                )}
              </React.Fragment>
            )}

            {order.type === ORDER_TYPES.booking && (
              <div>
                <div className="entry__label">{app_main_wpml.date_hour}</div>
                <div className="entry__value">
                  {formatDateTime(order.bookingDatetime)}
                </div>
              </div>
            )}
            {order.type === ORDER_TYPES.voucher && (
              <div>
                <div className="entry__label">
                  {app_main_wpml.expiration_date}
                </div>
                <div className="entry__value">
                  {formatDate(order.voucherExpiry)}
                </div>
              </div>
            )}

            <div className="entry-order-items">
              <div className="entry__label">{app_main_wpml.Participants}</div>
              <div className="entry__value">
                {order.items
                  .filter((item) => item.type === 'seat')
                  .map((item, index) => (
                    <OrderItem key={index} {...item} />
                  ))}
              </div>
            </div>

            {(function () {
              if (!order.items || !order.items.length) {
                return null;
              }
              const filtered = order.items.filter(
                (item) => item.type === 'optional'
              );
              if (!filtered.length) {
                return null;
              }
              return (
                <div className="entry-order-items">
                  <div className="entry__label">{app_main_wpml.services}</div>
                  <div className="entry__value">
                    {filtered.map((item, index) => (
                      <OrderItem key={index} {...item} />
                    ))}
                  </div>
                </div>
              );
            })()}

            {!!order.discounts && !!order.discounts.length && (
              <div className="entry-order-items">
                <div className="entry__label">{app_main_wpml.discounts}</div>
                <div className="entry__value">
                  {order.discounts.map((discount, index) => (
                    <OrderItem
                      key={index}
                      label={discount.code}
                      price={-discount.discount}
                      quantity={0}
                    />
                  ))}
                </div>
              </div>
            )}

            {order.type === ORDER_TYPES.voucher &&
              order.voucherMessage !== null && (
                <>
                  <div className="entry-voucher-details">
                    <div className="entry__label">
                      {app_main_wpml.dedicated_to}
                    </div>
                    <div className="entry__value entry__value__name">
                      {order.voucherMessage.name !== null
                        ? order.voucherMessage.name
                        : ''}
                    </div>
                    <div className="entry__value entry__value__message">
                      {order.voucherMessage.message !== null
                        ? order.voucherMessage.message
                        : ''}
                    </div>
                  </div>
                </>
              )}
          </div>

          {(!!order.refunds && !!order.refunds.length) ||
            order.onsitePayment ? (
            <>
              <div className="entry-refund mb-3xs">
                <div>
                  <div className="entry__label">
                    {app_main_wpml.charge} - {formatDate(order.createdAt)}
                  </div>
                  <div className="entry__value">
                    <strong>
                      {order.totalRefund
                        ? formatCurrency(order.total + order.totalRefund)
                        : formatCurrency(order.total)}
                    </strong>
                  </div>
                </div>
              </div>

              {order.refunds &&
                order.refunds.map((refund, index) => (
                  <div key={index} className="entry-refund">
                    <div>
                      <div className="entry__label">{app_main_wpml.refund}</div>
                      <div className="entry__value">
                        -{formatCurrency(refund.total)}
                      </div>
                    </div>
                  </div>
                ))}
              {order.onsitePayment && (
                <div className="entry-refund">
                  <div>
                    <div className="entry__label">
                      {TRANSLATIONS.PAYMENT_ON_SITE}
                    </div>
                    <div className="entry__value">
                      <strong>+{formatCurrency(order.onsitePayment)}</strong>
                    </div>
                  </div>
                </div>
              )}
              <div className="entry-total">
                <div className="entry__label">{app_main_wpml.total}</div>
                <div className="entry__value">
                  {!!order.onsitePayment
                    ? formatCurrency(order.total + order.onsitePayment)
                    : formatCurrency(order.total)}
                </div>
              </div>
            </>
          ) : (
            <div className="entry-total">
              <div className="entry__label">{app_main_wpml.total}</div>
              <div className="entry__value">{formatCurrency(order.total)}</div>
            </div>
          )}
        </section>

        {canShowPartnerInfo(order.status) && !!partner && (
          <section className="order-details__section">
            <h2 className="order-details__section__title">
              {app_main_wpml.partner_contacts}
            </h2>
            <div className="entry-partner">
              <div>
                <div className="entry__label">{app_main_wpml.first_name}</div>
                <div className="entry__value">{`${partner.firstName} ${partner.lastName} - ${partner.name}`}</div>
              </div>
              <div>
                <div className="entry__label">{app_main_wpml.phone}</div>
                <div className="entry__value">{partner.phone}</div>
              </div>
              <div>
                <div className="entry__label">{app_main_wpml.Email}</div>
                <div className="entry__value">{partner.email}</div>
              </div>
            </div>
          </section>
        )}

        {/* order status */}
        <section className="order-details__section">
          <h2 className="order-details__section__title">
            {app_main_wpml.reservation_status}
          </h2>

          <OrderStatusInfo order={order} showDescription={true} />

          {order.type === ORDER_TYPES.voucher &&
            (!order.customerRequestStatus ||
              order.customerRequestStatus !== 'pending') && (
              <>
                <div>
                  <div className="entry__label">
                    {app_main_wpml.expiration_date}
                  </div>
                  <div className="entry__value">
                    {formatDate(order.voucherExpiry)}
                  </div>
                </div>
                {order.status === ORDER_STATUSES.confirmedVoucher && (
                  <div>
                    <div className="entry__label">
                      {app_main_wpml.secret_code}
                    </div>
                    <div className="entry__value">{order.redeemKey}</div>
                  </div>
                )}
              </>
            )}
        </section>

        {!!order.customerRequestStatus &&
          order.customerRequestStatus !== 'rejected' &&
          !(
            order.customerRequestStatus === 'approved' &&
            REQUEST_TYPES.refund.includes(order.customerRequestType)
          ) &&
          requestStatusesToShowCard.includes(order.customerRequestType) && (
            <div className="order-details__section">
              <ModalOrderCard
                order={order}
                toBeRefunded={
                  order.total > 0 &&
                  REQUEST_TYPES.refund.includes(order.customerRequestType)
                }
                toReschedule={REQUEST_TYPES.reschedule.includes(
                  order.customerRequestType
                )}
                toVoucher={REQUEST_TYPES.voucher.includes(
                  order.customerRequestType
                )}
                motivation={order?.customerRequestReason || null}
                newDate={
                  order?.customerRequestRequestedDatetime
                    ? moment(
                      order.customerRequestRequestedDatetime,
                      'YYYY-MM-DD HH:mm:ss'
                    ).format('DD/MM/YYYY HH:mm')
                    : null
                }
              />
            </div>
          )}

        {(!!order.refunds && !!order.refunds.length) ||
          (order.onsitePayment && (
            <section className="order-details__section">
              <h2 className="order-details__section__title">
                {app_main_wpml.payments_history}
              </h2>
              <div className="entry-refund">
                <div>
                  <div className="entry__label">
                    {app_main_wpml.charge} - {formatDate(order.createdAt)}
                  </div>
                  <div className="entry__value">
                    {formatCurrency(order.total + order.totalRefund)}
                  </div>
                </div>
              </div>

              {order.refunds.map((refund, index) => (
                <div key={index} className="entry-refund">
                  <div>
                    <div className="entry__label">
                      {app_main_wpml.refund} - {formatDate(refund.date)}
                    </div>
                    <div className="entry__value">
                      -{formatCurrency(refund.total)}
                    </div>
                  </div>
                </div>
              ))}

              <div className="entry-refund">
                <div>
                  <div className="entry__label">{app_main_wpml.totalpayed}</div>
                  <div className="entry__value">
                    <strong>{formatCurrency(order.total)}</strong>
                  </div>
                </div>
              </div>

              {order.onsitePayment && (
                <div className="entry-refund">
                  <div>
                    <div className="entry__label">
                      {TRANSLATIONS.PAYMENT_ON_SITE}
                    </div>
                    <div className="entry__value">
                      <strong>+{formatCurrency(order.onsitePayment)}</strong>
                    </div>
                  </div>
                </div>
              )}

              {order.refunds && !!order.refunds.length && (
                <div className="entry-tip mb-xs">
                  {app_main_wpml.refund_time}
                </div>
              )}
              {order.onsitePayment && (
                <div className="entry-tip">
                  {TRANSLATIONS.YOU_CAN_PAY_THE_DIFFERENCE_ON_SITE}
                </div>
              )}
            </section>
          ))}

        <div className="order-details__section">
          {!!acceptedTerms && acceptedTerms.length != 0 ? (
            <a onClick={handleOpenTerms}>{app_main_wpml.accepted_terms}</a>
          ) : (
            <a className="disabled">{app_main_wpml.accepted_terms}</a>
          )}
        </div>

        <div className="entry-cta">
          <a className="btn" onClick={onCloseClick}>
            {app_main_wpml.close}
          </a>
        </div>
      </div>
    </>
  );
};
OrderDetails.defaultProps = {
  onCloseClick: () => { },
  order: {},
};
OrderDetails.propTypes = {
  onCloseClick: PropTypes.func,
  order: PropTypes.shape({
    id: PropTypes.number,
    bookingDatetime: PropTypes.instanceOf(moment),
    createdAt: PropTypes.instanceOf(moment),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        label: PropTypes.string,
        price: PropTypes.number,
        quantity: PropTypes.number,
        type: PropTypes.string,
      })
    ),
    listing: PropTypes.object,
    partner: PropTypes.shape({
      email: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      phone: PropTypes.string,
    }),
    redeemKey: PropTypes.string,
    refunds: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.instanceOf(moment),
        reason: PropTypes.string,
        total: PropTypes.number,
      })
    ),
    status: PropTypes.oneOf(Object.values(ORDER_STATUSES)),
    total: PropTypes.number,
    totalDiscount: PropTypes.number,
    totalRefund: PropTypes.number,
    type: PropTypes.string,
    voucherExpiry: PropTypes.instanceOf(moment),
    voucherMessage: PropTypes.shape({
      message: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

export default OrderDetails;
